import { useEffect, useState, FC, useCallback, Fragment } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  CircularProgress,
  Typography,
  Box,
  TableRowProps,
  styled,
  useTheme
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { MenuItem, MenuItemPermissions } from '../../types/permission.types';
import { menuApi } from '../../api/menuApi';

// Local type that matches the API response
interface ApiMenuItem extends Omit<MenuItem, 'children' | 'permissions' | 'icon'> {
  icon?: string | null;
  permissions?: {
    view: string;
    create: string;
    edit: string;
    delete: string;
  };
  children?: ApiMenuItem[];
}

interface MenuItemWithPermissions extends Omit<MenuItem, 'permissions' | 'children' | 'icon'> {
  icon?: string;
  permissions: MenuItemPermissions;
  children?: MenuItemWithPermissions[];
}

const IndentedTableRow = styled(TableRow)<TableRowProps & { level?: number }>(({ level = 0, theme }) => ({
  '& > td:first-of-type': {
    paddingLeft: theme.spacing(2 + (level * 2)),
  }
}));

interface RolePermissionsDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (permissions: Record<string, MenuItemPermissions>) => Promise<void>;
  roleId: string;
  roleName: string;
  isSystem: boolean;
}

const RolePermissionsDialog: FC<RolePermissionsDialogProps> = ({
  open,
  onClose,
  onSave,
  roleId,
  roleName,
  isSystem
}) => {
  const theme = useTheme();
  const [menuItems, setMenuItems] = useState<MenuItemWithPermissions[]>([]);
  const [permissions, setPermissions] = useState<Record<string, MenuItemPermissions>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { enqueueSnackbar } = useSnackbar();

  // Permission types to display
  const permissionTypes: Array<{
    key: keyof MenuItemPermissions;
    label: string;
  }> = [
    { key: 'canView', label: 'View' },
    { key: 'canCreate', label: 'Create' },
    { key: 'canEdit', label: 'Edit' },
    { key: 'canDelete', label: 'Delete' },
  ];

  // Handle permission changes
  const handlePermissionChange = useCallback((
    menuId: string,
    permissionType: keyof MenuItemPermissions,
    value: boolean
  ) => {
    setPermissions(prev => ({
      ...prev,
      [menuId]: {
        ...(prev[menuId] || {
          canView: false,
          canCreate: false,
          canEdit: false,
          canDelete: false
        }),
        [permissionType]: value
      }
    }));
  }, []);

  // Process menu items from API response
  const processMenuItems = useCallback((items: ApiMenuItem[]): MenuItemWithPermissions[] => {
    return items.map(menu => {
      const menuItem: MenuItemWithPermissions = {
        id: menu.id,
        title: menu.title,
        path: menu.path,
        order: menu.order,
        parentId: menu.parentId,
        isGroup: menu.isGroup || false,
        isSystem: menu.isSystem || false,
        isActive: menu.isActive !== false,
        permissions: menu.permissions ? {
          canView: menu.permissions.view === 'true',
          canCreate: menu.permissions.create === 'true',
          canEdit: menu.permissions.edit === 'true',
          canDelete: menu.permissions.delete === 'true'
        } : {
          canView: false,
          canCreate: false,
          canEdit: false,
          canDelete: false
        }
      };

      // Only add icon if it exists
      if (menu.icon) {
        menuItem.icon = menu.icon;
      }

      // Process children recursively if they exist
      if (menu.children) {
        menuItem.children = processMenuItems(menu.children);
      }

      return menuItem;
    });
  }, []);

  // Load menu permissions
  const loadMenuPermissions = useCallback(async () => {
    try {
      setIsLoading(true);
      const data = await menuApi.getMenuPermissions();
      // Ensure data is an array before processing
      const menuData = Array.isArray(data) ? data : [];
      const formattedItems = processMenuItems(menuData);
      setMenuItems(formattedItems);
      
      // Initialize permissions with default values
      const initialPermissions: Record<string, MenuItemPermissions> = {};
      const setPermissionsRecursively = (items: MenuItemWithPermissions[]) => {
        items.forEach(item => {
          initialPermissions[item.id] = item.permissions;
          if (item.children) {
            setPermissionsRecursively(item.children);
          }
        });
      };
      setPermissionsRecursively(formattedItems);
      setPermissions(initialPermissions);
    } catch (error) {
      console.error('Error loading menu permissions:', error);
      setError('Failed to load menu permissions');
      enqueueSnackbar('Failed to load menu permissions', { variant: 'error' });
    } finally {
      setIsLoading(false);
    }
  }, [processMenuItems, enqueueSnackbar]);

  // Load menu permissions when the dialog opens
  useEffect(() => {
    if (!open) return;
    
    const loadMenus = async () => {
      try {
        await loadMenuPermissions();
      } catch (error) {
        console.error('Failed to load menus:', error);
      }
    };
    
    loadMenus();
  }, [open, loadMenuPermissions]);

  // Group menu items by parent
  const menuGroups = useCallback(() => {
    return menuItems.reduce((groups, item) => {
      const groupId = item.parentId || 'root';
      if (!groups[groupId]) {
        groups[groupId] = [];
      }
      groups[groupId].push(item);
      return groups;
    }, {} as Record<string, MenuItemWithPermissions[]>);
  }, [menuItems]);

  // Get root menu items (no parent)
  const rootMenus = menuGroups()['root'] || [];

  // Handle save button click
  const handleSave = useCallback(async () => {
    try {
      setIsSaving(true);
      await onSave(permissions);
      enqueueSnackbar('Permissions saved successfully', { variant: 'success' });
      onClose();
    } catch (error) {
      console.error('Failed to save permissions:', error);
      enqueueSnackbar('Failed to save permissions', { variant: 'error' });
    } finally {
      setIsSaving(false);
    }
  }, [permissions, roleId, onClose, enqueueSnackbar, setIsSaving]);

  // Close the dialog
  const handleClose = useCallback(() => {
    if (!isSaving) {
      onClose();
    }
  }, [isSaving, onClose]);

  // Render menu items recursively
  const renderMenuItems = (items: MenuItemWithPermissions[], level = 0) => {
    return items.map(item => (
      <Fragment key={item.id}>
        <IndentedTableRow level={level}>
          <TableCell>{item.title}</TableCell>
          {permissionTypes.map(({ key }) => (
            <TableCell key={key} align="center">
              <Checkbox
                checked={permissions[item.id]?.[key] || false}
                onChange={(e) => handlePermissionChange(item.id, key, e.target.checked)}
                disabled={isSystem}
                color="primary"
              />
            </TableCell>
          ))}
        </IndentedTableRow>
        {item.children && renderMenuItems(item.children, level + 1)}
      </Fragment>
    ));
  };

  // Render the dialog content
  const renderDialogContent = () => {
    if (isLoading) {
      return (
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      );
    }

    if (error) {
      return (
        <Box p={2}>
          <Typography color="error">{error}</Typography>
        </Box>
      );
    }

    return (
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Menu Item</TableCell>
              {permissionTypes.map(({ key, label }) => (
                <TableCell key={key} align="center">
                  {label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {renderMenuItems(rootMenus)}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  // Only render the dialog if we have data
  if (!open) return null;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
      <DialogTitle>Manage Permissions - {roleName}</DialogTitle>
      <DialogContent>
        {renderDialogContent()}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isSaving || isSystem}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          color="primary"
          variant="contained"
          disabled={isSaving || isSystem}
          startIcon={isSaving ? <CircularProgress size={20} /> : undefined}
        >
          {isSaving ? 'Saving...' : 'Save Permissions'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RolePermissionsDialog;
